import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Buildings, Lock, EnvelopeSimple, User, ShieldCheck } from "@phosphor-icons/react";

const BG = "https://images.unsplash.com/photo-1497215842964-222b430dc094?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjB3b3Jrc3BhY2UlMjBtaW5pbWFsfGVufDB8fHx8MTc4NjE2MTIyM3ww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingMsg, setPendingMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = mode === "login"
      ? await login(email, password)
      : await register(name, email, password);
    setBusy(false);
    if (res.ok) {
      if (res.pending) {
        setPendingMsg(res.message || "Your account is awaiting administrator approval.");
        setMode("login");
        setPassword("");
        toast.success("Account created — awaiting admin approval");
        return;
      }
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Visual side */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={BG} alt="Workspace" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/70 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20">
              <Buildings weight="duotone" className="h-6 w-6" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-heading font-extrabold text-xl tracking-tight">Casedist Inc</span>
              <span className="text-sm text-white/70 font-medium">HR Management</span>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
              People operations, done right.
            </h1>
            <p className="mt-5 text-white/80 text-base leading-relaxed">
              A secure portal for your employee directory, leave management, and confidential records — with strict role-based access.
            </p>
            <div className="mt-8 flex items-center gap-3 text-white/80 text-sm">
              <ShieldCheck weight="duotone" className="h-5 w-5" />
              <span>Sensitive files are visible to admins only.</span>
            </div>
          </div>
          <div className="text-white/50 text-xs">© {new Date().getFullYear()} Casedist Inc.</div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <Buildings weight="duotone" className="h-6 w-6 text-primary-foreground" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-heading font-extrabold text-xl">Casedist Inc</span>
              <span className="text-sm text-muted-foreground font-medium">HR Management</span>
            </div>
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            {mode === "login" ? "Sign in to your account" : "Create your account"}
          </h2>
          <p className="text-muted-foreground text-sm mt-2">
            {mode === "login" ? "Enter your credentials to continue." : "Fill in your details to get started."}
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="auth-form">
            {pendingMsg && (
              <div className="rounded-lg border border-warning/30 bg-warning/10 p-4 text-sm text-foreground flex items-start gap-2.5" data-testid="pending-approval-notice">
                <ShieldCheck weight="duotone" className="h-5 w-5 text-warning shrink-0 mt-0.5" />
                <span>{pendingMsg}</span>
              </div>
            )}
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="name" data-testid="name-input" className="pl-9" placeholder="Jane Cooper"
                    value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="email" data-testid="email-input" type="email" className="pl-9" placeholder="you@company.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="password" data-testid="password-input" type="password" className="pl-9" placeholder="••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
            </div>

            <Button type="submit" data-testid="submit-button" disabled={busy}
              className="w-full h-11 rounded-lg font-semibold">
              {busy ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs uppercase tracking-[0.2em]">
              <span className="bg-background px-3 text-muted-foreground">or</span>
            </div>
          </div>

          <Button type="button" variant="outline" data-testid="google-login-button" onClick={handleGoogle}
            className="w-full h-11 rounded-lg font-medium gap-2">
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>

          <p className="text-sm text-center text-muted-foreground mt-6">
            {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
            <button type="button" data-testid="toggle-mode-button"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-primary font-semibold hover:underline">
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
